import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserModuleRolePermission } from "../types/UserModuleRolePermission";
import { buildMenuFromPermissions } from "../utils/menuHelper";
import useApi from "../hooks/useApi";
import { useUser } from "./UserContext";
import { getUserModuleRolePermissions } from "../services/portalUserService";
import { IMPERSONATION_INFO } from "../data/constants";

const MAX_PERMISSION_FETCH_ATTEMPTS = 3;
const PERMISSION_FETCH_RETRY_DELAY_MS = 1000;

type MenuItem = ReturnType<typeof buildMenuFromPermissions>;

interface MenuContextType {
  menu: MenuItem;
  permissions: UserModuleRolePermission[];
  loading: boolean;
}

const MenuContext = createContext<MenuContextType>({ menu: [], permissions: [], loading: true });

export const useMenu = () => useContext(MenuContext);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [menu, setMenu] = useState<MenuItem>([]);
  const [permissions, setPermissions] = useState<UserModuleRolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const { executeApi } = useApi();
  const { userId } = useUser();
  // Tracks the latest fetch run so a stale retry (e.g. from a previous userId)
  // can bail out instead of overwriting fresh state.
  const fetchRunIdRef = useRef(0);

  const fetchPermissions = useCallback(async () => {
    if (!userId || userId <= 0) {
      return;
    }

    const runId = ++fetchRunIdRef.current;
    setLoading(true);

    // Impersonated JWTs are freshly issued by the server and immediately valid —
    // the "token still settling" scenario that retries guard against cannot occur.
    // Skip retries to avoid multiplying an already-slow impersonation permission
    // fetch by MAX_PERMISSION_FETCH_ATTEMPTS.
    const isImpersonating = localStorage.getItem(IMPERSONATION_INFO.IsImpersonating) === "true";
    const maxAttempts = isImpersonating ? 1 : MAX_PERMISSION_FETCH_ATTEMPTS;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const userModuleRolePermissions = await executeApi<number, UserModuleRolePermission[]>(
        getUserModuleRolePermissions,
        userId
      );

      // A newer fetch started (userId changed) — abandon this stale run.
      if (runId !== fetchRunIdRef.current) {
        return;
      }

      if (userModuleRolePermissions?.length > 0) {
        setPermissions(userModuleRolePermissions);
        setMenu(buildMenuFromPermissions(userModuleRolePermissions));
        setLoading(false);
        return;
      }

      // Empty/failed result (e.g. auth token still settling right after login).
      // Retry before giving up so the UI isn't left permanently without permissions.
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, PERMISSION_FETCH_RETRY_DELAY_MS));
      }
    }

    if (runId === fetchRunIdRef.current) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return (
    <MenuContext.Provider value={{ menu, permissions, loading }}>{children}</MenuContext.Provider>
  );
};
